import { Dispatch, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DocStatus } from 'components/DocBox/DocBox.types';
import { dateFormatZurich } from 'utils/datetime';
import { getBase64FromFile } from 'utils/file';
import { notification } from 'ui-library';
import { ApplicationDetailsActionTypes, JAUsersType } from 'datasource/useApplicationDetails/ApplicationDetailsManager.types';
import { useGetIdDocuments } from 'hooks/rest/useGetIdDocuments';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';

/**
 * Owns the ID-document query for joint-account users, derives the reducer entries from it,
 * and exposes the upload handler. Extracted verbatim from ApplicationDetailsManager.
 */
export const useIdDocuments = ({
    dispatch,
    jaUsers,
}: { dispatch: Dispatch<any>; jaUsers?: JAUsersType }) => {
    const { i18n: { language }, t } = useTranslation();
    const { mutateAsync: handleMutation } = useGenericAPIMutation();

    const {
        data: idDocumentsData,
    } = useGetIdDocuments({ jaUsers: jaUsers as JAUsersType, language, isEnabled: !!jaUsers?.length });

    useEffect(() => {
        if (!jaUsers?.length) return;
        const responses = idDocumentsData as Array<any> | null;

        if (!responses) return;

        if (responses?.[0]) {
            dispatch({
                type: ApplicationDetailsActionTypes.SET_ID_DOCUMENTS,
                payload: responses?.map(res => ({
                    contactId: res?.userId,
                    documentId: res?.id,
                    documentName: res?.metadataName || res?.name || t('ApplicationDetails.IDDocument'),
                    status: res?.status || DocStatus.ReadyToSubmit,
                    date: res.createDate ? moment(res.createDate)?.format(dateFormatZurich) : null,
                })),
            });
        } else {
            dispatch({
                type: ApplicationDetailsActionTypes.SET_ID_DOCUMENTS,
                payload: (jaUsers as JAUsersType)?.map(res => ({
                    contactId: res?.id,
                    documentId: null,
                    documentName: t('ApplicationDetails.IDDocument'),
                    status: DocStatus.ReadyToSign,
                })),
            });
        }
    }, [idDocumentsData, jaUsers, language]);

    const onIdDocumentUpload = useCallback(async ({ userId, docData }) => {
        let payload;

        try {
            payload = {
                ownerId: userId,
                documents: await Promise.all(docData?.file?.map(async (file) => ({
                    filename: file.name,
                    file: (await getBase64FromFile(file)).split(',')[1],
                }))),
            };
        } catch (err: any) {
            notification.open({ content: `${t('contactGroups.somethingWentWrong')} ${err.message}`, type: 'error' });

            return;
        }

        // useGenericAPIMutation shows its own error notification; swallow the rejection here
        // so a failed upload does not surface a duplicate toast / unhandled rejection.
        const response = await handleMutation({
            service: 'customDocumentsService',
            API: 'uploadIdDocument',
            payload,
            invalidateQueryKeys: ['getIdDocuments'],
        }).catch(() => undefined);
        const apiData = (response as any)?.data;

        if (apiData) {
            dispatch({
                type: ApplicationDetailsActionTypes.UPDATE_ID_DOCUMENTS,
                payload: {
                    contactId: userId,
                    documentId: apiData,
                    documentName: t('ApplicationDetails.IDDocument'),
                    status: DocStatus.ReadyToSubmit,
                    date: moment().format(dateFormatZurich),
                },
            });
        }
        // invalidateQueryKeys: ['getIdDocuments'] above already refetches the active query — no manual refetch needed.
    }, [handleMutation, t]);

    return { onIdDocumentUpload };
};
